import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Check, X, Trash2 } from "lucide-react";
import { api, formatErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { fmtDate } from "../lib/format";
import { PageHeader, StatusBadge, EmptyState } from "../components/Common";
import { LeaveBalanceCards } from "../components/LeaveBalanceCards";
import { LeaveHrTools } from "../components/LeaveHrTools";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";

const PENDING = ["pending_manager", "pending_hr"];
const TAB_LABELS = { pending_hr: "Awaiting HR", pending_manager: "Awaiting manager", approved: "Approved", rejected: "Rejected", all: "All", team: "Team approvals" };

const ApproveReject = ({ l, fn, prefix }) => (
  <>
    <Button size="sm" variant="ghost" className="text-emerald-700" onClick={() => fn(l, "approved")} data-testid={`${prefix}-approve-leave-${l.id}`}><Check className="h-4 w-4 mr-1" />Approve</Button>
    <Button size="sm" variant="ghost" className="text-red-600" onClick={() => fn(l, "rejected")} data-testid={`${prefix}-reject-leave-${l.id}`}><X className="h-4 w-4 mr-1" />Reject</Button>
  </>
);

const ApplyDialog = ({ open, onOpenChange, onSaved }) => {
  const [f, setF] = useState({ leave_type: "casual", start_date: "", end_date: "", reason: "" });
  const [busy, setBusy] = useState(false);
  const [types, setTypes] = useState([]);
  useEffect(() => {
    if (open) api.get("/leave-types").then((r) => setTypes(r.data.filter((t) => t.active)));
  }, [open]);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/leaves", f);
      toast.success("Leave request submitted");
      setF({ leave_type: "casual", start_date: "", end_date: "", reason: "" });
      onSaved();
      onOpenChange(false);
    } catch (err) { toast.error(formatErr(err)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white" data-testid="apply-leave-dialog">
        <DialogHeader><DialogTitle>Apply for leave</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5"><Label>Leave type</Label>
            <Select value={f.leave_type} onValueChange={(v) => setF({ ...f, leave_type: v })}>
              <SelectTrigger data-testid="leave-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>{types.map((t) => <SelectItem key={t.code} value={t.code}>{t.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>From</Label><Input type="date" required value={f.start_date} onChange={(e) => setF({ ...f, start_date: e.target.value })} data-testid="leave-start-input" /></div>
            <div className="space-y-1.5"><Label>To</Label><Input type="date" required value={f.end_date} onChange={(e) => setF({ ...f, end_date: e.target.value })} data-testid="leave-end-input" /></div>
          </div>
          <div className="space-y-1.5"><Label>Reason</Label><Textarea value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} data-testid="leave-reason-input" /></div>
          <Button type="submit" disabled={busy} className="w-full bg-[#E08A1E] hover:bg-[#C87515] text-white" data-testid="leave-submit-button">{busy ? "Submitting..." : "Submit request"}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default function Leaves() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const [list, setList] = useState([]);
  const [bal, setBal] = useState(null);
  const [tab, setTab] = useState(isAdmin ? "pending_hr" : "all");
  const [open, setOpen] = useState(false);
  const tabs = [...(isAdmin ? ["pending_hr", "pending_manager", "approved", "rejected", "all"] : ["all", "pending_manager", "pending_hr", "approved", "rejected"]), ...(user.is_manager ? ["team"] : [])];
  const showEmp = isAdmin || tab === "team";

  const load = () => {
    const params = tab === "all" ? {} : tab === "team" ? { scope: "team", status: "pending_manager" } : { status: tab };
    api.get("/leaves", { params }).then((r) => setList(r.data));
    api.get("/leaves/balance").then((r) => setBal(r.data));
  };
  useEffect(() => {
    load();
  }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  const decide = async (l, status) => {
    try { await api.put(`/leaves/${l.id}/decision`, { status, note: "" }); toast.success(`Leave ${status}`); load(); }
    catch (err) { toast.error(formatErr(err)); }
  };
  const managerDecide = async (l, status) => {
    try { await api.put(`/leaves/${l.id}/manager-decision`, { status, note: "" }); toast.success(status === "approved" ? "Approved and sent to HR" : "Leave rejected"); load(); }
    catch (err) { toast.error(formatErr(err)); }
  };
  const cancel = async (l) => {
    try { await api.delete(`/leaves/${l.id}`); toast.success("Leave request cancelled"); load(); }
    catch (err) { toast.error(formatErr(err)); }
  };

  return (
    <div data-testid="leaves-page">
      <PageHeader eyebrow="Time off" title="Leave management" subtitle="Requests go to the reporting manager first, then HR gives final approval. Company holidays and weekly off days are not counted as leave days."
        actions={<Button onClick={() => setOpen(true)} data-testid="apply-leave-button" className="bg-[#E08A1E] hover:bg-[#C87515] text-white rounded-full"><Plus className="h-4 w-4 mr-1" /> Apply leave</Button>} />
      {bal && <LeaveBalanceCards bal={bal} />}
      {isAdmin && <LeaveHrTools onChange={load} />}
      <Tabs value={tab} onValueChange={setTab} className="mb-4">
        <TabsList className="flex-wrap h-auto">{tabs.map((s) => <TabsTrigger key={s} value={s} data-testid={`leave-tab-${s}`}>{!isAdmin && s === "all" ? "My requests" : TAB_LABELS[s]}</TabsTrigger>)}</TabsList>
      </Tabs>
      {!list.length ? <EmptyState text="No leave requests here" testid="leaves-empty" /> : (
        <div className="bg-white border border-[#E5E5EA] rounded-2xl overflow-x-auto">
          <Table data-testid="leaves-table">
            <TableHeader><TableRow>{showEmp && <TableHead>Employee</TableHead>}<TableHead>Type</TableHead><TableHead>Dates</TableHead><TableHead>Days</TableHead><TableHead>Reason</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
            <TableBody>{list.map((l) => (
              <TableRow key={l.id} data-testid={`leave-row-${l.id}`}>
                {showEmp && <TableCell><div className="font-semibold">{l.user_name}</div><div className="text-xs text-[#6E6E73]">{l.employee_code}</div></TableCell>}
                <TableCell>{l.leave_type_name || l.leave_type}</TableCell>
                <TableCell className="whitespace-nowrap">{fmtDate(l.start_date)} → {fmtDate(l.end_date)}</TableCell>
                <TableCell>{l.days}</TableCell>
                <TableCell className="max-w-[220px] truncate">{l.reason || "—"}</TableCell>
                <TableCell>
                  <StatusBadge status={l.status} testid={`leave-status-${l.id}`} />
                  {l.manager_name && <div className="text-[11px] text-[#6E6E73] mt-1">Manager: {l.manager_name}{l.manager_decision ? ` (${l.manager_decision})` : ""}</div>}
                  {l.decided_by && <div className="text-[11px] text-[#6E6E73]">HR: {l.decided_by}</div>}
                </TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  {tab === "team" && l.status === "pending_manager" ? <ApproveReject l={l} fn={managerDecide} prefix="manager" />
                    : isAdmin && l.status === "pending_hr" ? <ApproveReject l={l} fn={decide} prefix="hr" />
                    : isAdmin && l.status === "pending_manager" ? <Button size="sm" variant="ghost" className="text-red-600" onClick={() => decide(l, "rejected")} data-testid={`hr-reject-leave-${l.id}`}><X className="h-4 w-4 mr-1" />Reject</Button>
                    : null}
                  {PENDING.includes(l.status) && l.user_id === user.id && <Button size="sm" variant="ghost" onClick={() => cancel(l)} data-testid={`cancel-leave-${l.id}`}><Trash2 className="h-4 w-4" /></Button>}
                </TableCell>
              </TableRow>
            ))}</TableBody>
          </Table>
        </div>
      )}
      <ApplyDialog open={open} onOpenChange={setOpen} onSaved={load} />
    </div>
  );
}
